/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import * as Sentry from "@sentry/react";

import callLambda from "@lib/callLambda";
import { getUserFromLocalStorage } from "@lib/utils";

export type LogLevel = "ERROR" | "WARN" | "INFO" | "DEBUG";

export interface LogExtra {
    [key: string]: any;
}

export interface UserActionDetails extends LogExtra {
    userId?: number;
    sessionId?: string;
    [key: string]: any;
}

export interface PerformanceMetric {
    metric: string;
    value: number;
    timestamp: string;
    component?: string;
    extra?: LogExtra;
}

export interface ComponentLogger {
    error: (message: string, extra?: LogExtra) => void;
    warn: (message: string, extra?: LogExtra) => void;
    info: (message: string, extra?: LogExtra) => void;
    debug: (message: string, extra?: LogExtra) => void;
    logUserAction: (action: string, details?: UserActionDetails) => void;
    logPerformance: (metric: string, value: number, extra?: LogExtra) => void;
}

class Logger {
    private readonly isProduction: boolean;
    private readonly loggingEndpoint: string;
    private readonly userContext: { userId?: number; userEmail?: string } = {};

    constructor() {
        this.isProduction = process.env.BUILD_ENV === "production";
        this.loggingEndpoint =
            process.env.REACT_APP_ERROR_LOGGER_FUNCTION || "";

        // Get current user context if available
        const user = getUserFromLocalStorage();
        this.userContext = {
            userId: user?.id || undefined,
            userEmail: user?.email || undefined,
        };
    }

    private async logError(
        level: LogLevel,
        message: string,
        component: string | null = null,
        extra: LogExtra = {},
    ): Promise<void> {
        if (!this.isProduction) return;

        try {
            const logEvent = {
                timestamp: Date.now(),
                message: JSON.stringify({
                    level,
                    message,
                    component,
                    timestamp: new Date().toISOString(),
                    url: window.location.pathname,
                    userAgent: navigator.userAgent,
                    ...this.userContext,
                    ...extra,
                }),
            };

            callLambda(this.loggingEndpoint, logEvent);
        } catch (error) {
            console.error("Failed to send log to CloudWatch:", error);
        }
    }

    private formatLogMessage(
        component: string | null,
        message: string,
    ): string {
        return `[${component || "App"}] ${message}`;
    }

    public async error(
        message: string,
        extra: LogExtra = {},
        component: string | null = null,
    ): Promise<void> {
        const formattedMessage = this.formatLogMessage(component, message);

        if (this.isProduction) {
            // Send to Sentry for error tracking and alerts
            Sentry.captureException(new Error(message), {
                tags: { component: component || "unknown" },
                extra,
                level: "error",
            });

            this.logError("ERROR", message, component, extra);
        } else {
            console.error(formattedMessage, extra);
        }
    }

    public async warn(
        message: string,
        extra: LogExtra = {},
        component: string | null = null,
    ): Promise<void> {
        const formattedMessage = this.formatLogMessage(component, message);

        if (this.isProduction) {
            this.logError("WARN", message, component, extra);

            // Send critical warnings to Sentry too
            if (extra.critical) {
                Sentry.captureMessage(message, "warning");
            }
        } else {
            console.warn(formattedMessage, extra);
        }
    }

    public async info(
        message: string,
        extra: LogExtra = {},
        component: string | null = null,
    ): Promise<void> {
        const formattedMessage = this.formatLogMessage(component, message);

        if (this.isProduction) {
            this.logError("INFO", message, component, extra);
        } else {
            console.log(formattedMessage, extra);
        }
    }

    public debug(
        message: string,
        extra: LogExtra = {},
        component: string | null = null,
    ): void {
        if (!this.isProduction) {
            const formattedMessage = this.formatLogMessage(component, message);
            console.log(`[DEBUG] ${formattedMessage}`, extra);
        }
    }

    public async logUserAction(
        action: string,
        details: UserActionDetails = {},
        component: string | null = null,
    ): Promise<void> {
        const logData: UserActionDetails = {
            action,
            component,
            timestamp: new Date().toISOString(),
            url: window.location.pathname,
            userAgent: navigator.userAgent,
            ...this.userContext,
            ...details,
        };

        this.info(`User Action: ${action}`, logData, component);

        if (this.isProduction) {
            // Send to Sentry as breadcrumb for error context
            Sentry.addBreadcrumb({
                message: `User Action: ${action}`,
                category: "user-action",
                data: logData,
                level: "info",
            });
        }
    }

    public async logPerformance(
        metric: string,
        value: number,
        component: string | undefined = undefined,
        extra: LogExtra = {},
    ): Promise<void> {
        const perfData: PerformanceMetric = {
            metric,
            value,
            component,
            timestamp: new Date().toISOString(),
            ...extra,
        };

        this.info(`Performance: ${metric} = ${value}ms`, perfData, component);
    }

    public createComponentLogger(componentName: string): ComponentLogger {
        return {
            error: (message: string, extra: LogExtra = {}) =>
                this.error(message, extra, componentName),
            warn: (message: string, extra: LogExtra = {}) =>
                this.warn(message, extra, componentName),
            info: (message: string, extra: LogExtra = {}) =>
                this.info(message, extra, componentName),
            debug: (message: string, extra: LogExtra = {}) =>
                this.debug(message, extra, componentName),
            logUserAction: (action: string, details: UserActionDetails = {}) =>
                this.logUserAction(action, details, componentName),
            logPerformance: (
                metric: string,
                value: number,
                extra: LogExtra = {},
            ) => this.logPerformance(metric, value, componentName, extra),
        };
    }
}

// Export singleton instance
export const logger = new Logger();

// React hook for component-specific logging
export const useLogger = (componentName: string): ComponentLogger => {
    return logger.createComponentLogger(componentName);
};
